import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactElement} - AdminTariffList component
 */
function AdminTariffList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { console.log(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { tariffs } = viewer;

  const intl = useIntl();

  const copyLoadMore = intl.formatMessage({ id: 'admin.admin_tariff.admin_tariff_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'admin.admin_tariff.admin_tariff_list.load_more_button.state_no_more.label', defaultMessage: 'No more tariffs to load' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Enosi Admin - Tariffs</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: 'Admin', path: '/admin' },
          { name: 'Tariffs' },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">Tariffs</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Title</th>
                <th scope="col">Identifier</th>
                <th scope="col">ID</th>
                <th scope="col">
                  <abbr title="External Identifier">Ext ID</abbr>
                </th>
                <th scope="col">Tariff Type</th>
                <th scope="col">Meter</th>
                <th scope="col">Property</th>
              </tr>
            </thead>
            <tbody>
              {
                // tariffs && <pre>{JSON.stringify(tariffs, null, 2)}</pre>
                tariffs && tariffs.edges && tariffs.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    id, title, identifier, externalIdentifier, active,
                    tariffType, meter, property,
                  } = node;
                  const {
                    id: meterId, identifier: meterIdentifier, active: meterActive,
                  } = meter;
                  const {
                    id: propertyId, title: propertyTitle, active: propertyActive,
                  } = property;

                  return (
                    <tr key={`admin-tariff-${id}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={2}>
                        <Link to={`/admin/tariffs/${id}`}>
                          {title}
                        </Link>
                        <br />
                        {identifier}
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={id} />
                        <br />
                        {externalIdentifier}
                      </td>
                      <td>
                        {tariffType}
                      </td>
                      <td>
                        <Link to={`/admin/meters/${meterId}`}>
                          {meterIdentifier}
                        </Link>
                        <br />
                        <BadgeActive start={meterActive.start} finish={meterActive.finish} />
                      </td>
                      <td>
                        <Link to={`/admin/properties/${propertyId}`}>
                          {propertyTitle}
                        </Link>
                        <br />
                        <BadgeActive start={propertyActive.start} finish={propertyActive.finish} />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminTariffList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.shape({
    id: PropTypes.string,
    viewerUser: PropTypes.shape({
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
    }),
    tariffs: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            id: PropTypes.string,
            externalIdentifier: PropTypes.string,
            identifier: PropTypes.string,
            title: PropTypes.string,
            tariffType: PropTypes.string,
            active: PropTypes.shape({
              start: PropTypes.number,
              finish: PropTypes.number,
            }),
            meter: PropTypes.shape({
              id: PropTypes.string,
              identifier: PropTypes.string,
              active: PropTypes.shape({
                start: PropTypes.number,
                finish: PropTypes.number,
              }),
            }),
            property: PropTypes.shape({
              id: PropTypes.string,
              title: PropTypes.string,
              active: PropTypes.shape({
                start: PropTypes.number,
                finish: PropTypes.number,
              }),
              address: PropTypes.shape({
                line1: PropTypes.string,
                line2: PropTypes.string,
                city: PropTypes.string,
                state: PropTypes.string,
                postcode: PropTypes.string,
              }),
            }),
          }),
        }),
      ),
    }),
  }),
};

AdminTariffList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminTariffList,
  {
    viewer: graphql`
      fragment AdminTariffList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        tariffs(
          first: $count
          after: $cursor
        ) @connection(key: "AdminTariffList_tariffs") {
          edges {
            cursor
            node {
              id
              externalIdentifier
              identifier
              title
              tariffType
              active {
                start
                finish
              }
              meter {
                id
                identifier
                active { start finish }
              }
              property {
                id
                title
                active { start finish }
              }
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.tariffs;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminTariffList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminTariffList_viewer
      }
    }
  `,
  },
);
